/**
 * Page Header Component
 * 
 * Displays page title and optional subtitle with configurable styling.
 * 
 * **Features:**
 * - Configurable title size (sm, md, lg)
 * - Optional subtitle
 * - Alignment options (left, center)
 * - Full Tailwind customization support
 * 
 * **Usage:**
 * ```tsx
 * // Default - Hero style (matches booking flow)
 * <PageHeader 
 *   title="Review your selection"
 *   subtitle="Please review your subscription before proceeding"
 * />
 * 
 * // Smaller variant
 * <PageHeader 
 *   title="Complete your account"
 *   titleSize="md"
 * />
 * 
 * // Centered
 * <PageHeader 
 *   title="Success!"
 *   align="center"
 * />
 * ```
 * 
 * @module components/layouts
 */

import type { ReactNode } from 'react';

/**
 * Props for PageHeader component
 */
export interface PageHeaderProps {
  /**
   * Main page title (h1)
   */
  title: string;
  
  /**
   * Optional subtitle/description text
   */
  subtitle?: string | ReactNode;
  
  /**
   * Title size variant
   * 
   * - `sm`: text-2xl sm:text-3xl (compact headers)
   * - `md`: text-3xl sm:text-3xl (standard pages)
   * - `lg`: text-3xl sm:text-4xl (hero headers - default)
   * 
   * @default 'lg'
   */
  titleSize?: 'sm' | 'md' | 'lg';
  
  /**
   * Text alignment
   * 
   * @default 'left'
   */
  align?: 'left' | 'center';
  
  /**
   * Custom class for container
   */
  className?: string;
  
  /**
   * Custom class for title (h1)
   */
  titleClassName?: string;
  
  /**
   * Custom class for subtitle
   */
  subtitleClassName?: string;
}

/**
 * Title size mapping
 */
const titleSizeClasses = {
  sm: 'text-2xl sm:text-3xl',      // 24px mobile, 30px desktop
  md: 'text-3xl sm:text-4xl',      // 30px mobile, 36px desktop
  lg: 'text-4xl sm:text-5xl',      // 36px mobile, 48px desktop (Figma spec)
} as const;

/**
 * Page Header Component
 * 
 * Renders a page title with optional subtitle.
 * Provides consistent styling across the application while allowing customization.
 */
export function PageHeader({
  title,
  subtitle,
  titleSize = 'lg',
  align = 'left',
  className = '',
  titleClassName = '',
  subtitleClassName = '',
}: PageHeaderProps) {
  const alignClass = align === 'center' ? 'text-center' : 'text-left';
  
  return (
    <div className={`mb-8 ${alignClass} ${className}`}>
      <h1 
        className={`
          ${titleSizeClasses[titleSize]} 
          font-bold tracking-tight text-gray-900
          ${titleClassName}
        `}
        data-testid="subscription-page-header-title"
      >
        {title}
      </h1>
      
      {subtitle && (
        <p className={`mt-4 text-lg text-gray-600 ${subtitleClassName}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}
